import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  createScreenCaptureSession,
  type ScreenCaptureSession,
  type ScreenCaptureStartOptions,
  type ScreenCaptureState,
} from '@/audio/ScreenCaptureSession'

export interface UseScreenCaptureReturn {
  state: ScreenCaptureState
  start: (options: Omit<ScreenCaptureStartOptions, 'onStateChange'>) => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
}

/**
 * React lifecycle wrapper around createScreenCaptureSession().
 * Keeps a single session instance and cleans up on unmount.
 */
export function useScreenCapture(): UseScreenCaptureReturn {
  const sessionRef = useRef<ScreenCaptureSession | null>(null)
  const [state, setState] = useState<ScreenCaptureState>('idle')

  const getSession = useCallback(() => {
    if (!sessionRef.current) {
      sessionRef.current = createScreenCaptureSession()
    }
    return sessionRef.current
  }, [])

  useEffect(() => {
    return () => {
      sessionRef.current?.stop()
      sessionRef.current = null
    }
  }, [])

  const start = useCallback(
    async (options: Omit<ScreenCaptureStartOptions, 'onStateChange'>) => {
      const session = getSession()
      await session.start({
        ...options,
        onStateChange: setState,
        onError: options.onError,
      })
    },
    [getSession]
  )

  const pause = useCallback(() => {
    getSession().pause()
    setState(getSession().getState())
  }, [getSession])

  const resume = useCallback(() => {
    getSession().resume()
    setState(getSession().getState())
  }, [getSession])

  const stop = useCallback(() => {
    getSession().stop()
    setState('idle')
  }, [getSession])

  return useMemo(
    () => ({ state, start, pause, resume, stop }),
    [state, start, pause, resume, stop]
  )
}
